import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Download, Radio } from 'lucide-react';
import { adminAudit, adminSymbols } from '@/api/admin';
import type { FollowMode, Signal, SignalStatus } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pill } from '@/components/widgets/Pill';
import { useChannel } from '@/realtime/use-channel';
import { ApiError } from '@/lib/http';
import { downloadCsv } from '@/lib/csv';
import { SignalRow } from '@/features/signals/SignalRow';

const STATUS_FILTERS: { label: string; value: SignalStatus | 'ALL' }[] = [
  { label: '全部', value: 'ALL' },
  { label: '已成交', value: 'executed' },
  { label: '风控拦截', value: 'skipped_risk' },
  { label: '时段拦截', value: 'skipped_session' },
  { label: '用户停用', value: 'skipped_user_status' },
  { label: '失败', value: 'failed' },
];

const MODE_FILTERS: { label: string; value: FollowMode | 'ALL' }[] = [
  { label: '全部', value: 'ALL' },
  { label: 'REVERSE', value: 'REVERSE' },
  { label: 'FOLLOW', value: 'FOLLOW' },
];

const TIME_RANGES = [
  { label: '近 1 小时', hours: 1 },
  { label: '今日', hours: 24 },
  { label: '近 7 天', hours: 24 * 7 },
  { label: '全部', hours: 0 },
];

const PAGE_SIZE = 50;

export function AuditSignals() {
  const [status, setStatus] = useState<SignalStatus | 'ALL'>('ALL');
  const [hours, setHours] = useState<number>(24);
  const [mode, setMode] = useState<FollowMode | 'ALL'>('ALL');
  const [employeeAccountId, setEmployeeAccountId] = useState('');
  const [symbolId, setSymbolId] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [realtime, setRealtime] = useState<Signal[]>([]);

  const fromIso = useMemo(() => (hours > 0 ? new Date(Date.now() - hours * 3600_000).toISOString() : undefined), [hours]);

  const symbolsQ = useQuery({ queryKey: ['admin', 'symbols'], queryFn: adminSymbols.list });

  const trimmedEmp = employeeAccountId.trim();

  // 任一筛选条件变更都回到第 1 页(贴合 AlertsCenter 的分页交互)。
  const setStatusP = (v: typeof status) => { setStatus(v); setPage(1); };
  const setHoursP = (v: number) => { setHours(v); setPage(1); };
  const setModeP = (v: typeof mode) => { setMode(v); setPage(1); };
  const setEmpP = (v: string) => { setEmployeeAccountId(v); setPage(1); };
  const setSymbolP = (v: string) => { setSymbolId(v); setPage(1); };

  const listQ = useQuery({
    queryKey: ['admin', 'signals', status, hours, mode, trimmedEmp, symbolId, page],
    queryFn: () =>
      adminAudit.signals({
        page,
        pageSize: PAGE_SIZE,
        ...(status !== 'ALL' ? { status } : {}),
        ...(fromIso ? { from: fromIso } : {}),
        ...(mode !== 'ALL' ? { followMode: mode } : {}),
        ...(trimmedEmp ? { userId: trimmedEmp } : {}),
        ...(symbolId !== 'ALL' ? { symbolId } : {}),
      }),
    placeholderData: (prev) => prev,
  });

  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // WS 实时追加,合并去重
  const onSignal = useCallback((sig: Signal) => {
    setRealtime((prev) => {
      if (prev.some((p) => p.id === sig.id)) return prev;
      return [sig, ...prev].slice(0, 20);
    });
  }, []);
  useChannel('signals', onSignal, [onSignal]);

  const items = useMemo(() => {
    const fromQuery = listQ.data?.items ?? [];
    // 实时推送只在第 1 页顶部合并(深翻历史时不再插实时,避免每页都冒新条)。
    if (page !== 1) return fromQuery;
    const realtimeIds = new Set(realtime.map((s) => s.id));
    const dedupedQuery = fromQuery.filter((s) => !realtimeIds.has(s.id));
    let merged = [...realtime, ...dedupedQuery];
    // 实时推送来的 signal 也按当前筛选条件过滤,避免"风控拦截"页面突然冒一条"已成交"
    if (status !== 'ALL') merged = merged.filter((s) => s.status === status);
    if (mode !== 'ALL') merged = merged.filter((s) => s.followMode === mode);
    if (trimmedEmp) merged = merged.filter((s) => s.userId === trimmedEmp);
    if (symbolId !== 'ALL') merged = merged.filter((s) => s.symbolId === symbolId);
    return merged;
  }, [realtime, listQ.data, status, mode, trimmedEmp, symbolId, page]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Activity className="h-5 w-5 text-brand" />
            跟单审计
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            信号源 → 引擎决策 → 风控检查 → 主账户下单 → 成交确认 · 5 步链路完整可回溯(§9.8)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone="up">
            <Radio className="h-3 w-3 animate-pulse-glow" />
            实时推送 · 已接收 {realtime.length} 条
          </Pill>
          <Button
            variant="outline"
            size="sm"
            disabled={items.length === 0}
            title={`仅导出当前页 ${items.length} 条(每页 ${PAGE_SIZE} 条);翻页后再次导出可获取其余数据`}
            onClick={() =>
              downloadCsv('signals', items, [
                { key: 'createdAt', header: '时间' },
                { key: 'userId', header: '用户ID' },
                { key: 'userOrderId', header: '雇员订单号' },
                { key: 'userSide', header: '方向' },
                { key: 'userVolume', header: '手数', format: (v) => (Number(v) / 100).toFixed(2) },
                { key: 'followMode', header: '模式' },
                { key: 'followRatioUsed', header: '比例', format: (v) => v != null ? Number(v).toFixed(2) : '' },
                { key: 'realSide', header: '主账户方向' },
                { key: 'realVolume', header: '主账户手数', format: (v) => v != null ? (Number(v) / 100).toFixed(2) : '' },
                { key: 'status', header: '状态' },
                { key: 'skipReason', header: '原因' },
                { key: 'realOrderId', header: '主账户订单号' },
              ])
            }
          >
            <Download className="h-4 w-4" />
            导出当前页 CSV
          </Button>
        </div>
      </header>

      {/* Filter chips */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-3 mr-1">状态</span>
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              variant={status === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusP(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-3 mr-1">时间</span>
          {TIME_RANGES.map((t) => (
            <Button
              key={t.label}
              variant={hours === t.hours ? 'default' : 'outline'}
              size="sm"
              onClick={() => setHoursP(t.hours)}
            >
              {t.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-ink-3 mr-1">模式</span>
          {MODE_FILTERS.map((f) => (
            <Button
              key={f.value}
              variant={mode === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setModeP(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-3 mr-1">用户ID</span>
            <Input
              value={employeeAccountId}
              onChange={(e) => setEmpP(e.target.value)}
              placeholder="用户ID(回车筛选)"
              className="h-8 w-44"
            />
            {trimmedEmp && (
              <Button variant="ghost" size="sm" onClick={() => setEmpP('')}>
                清除
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-3 mr-1">品种</span>
            <select
              value={symbolId}
              onChange={(e) => setSymbolP(e.target.value)}
              className="h-8 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1"
            >
              <option value="ALL">全部</option>
              {(symbolsQ.data ?? []).map((s) => (
                <option key={s.symbolId} value={s.symbolId}>
                  {s.symbolName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            信号流水 · {listQ.data ? total : '—'} 条
            {total > 0 && (
              <span className="ml-2 text-xs text-ink-3 font-normal num">第 {page}/{totalPages} 页</span>
            )}
            <span className="ml-2 text-xs text-ink-3 font-normal">(展开行查看 5 步时间线)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isError && (
            <div className="px-4 py-3 text-sm text-down-dim">
              无法加载:{listQ.error instanceof ApiError ? listQ.error.message : '未知错误'}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-3 py-2 text-left font-normal">时间</th>
                  <th className="px-3 py-2 text-left font-normal">用户ID</th>
                  <th className="px-3 py-2 text-left font-normal">方向</th>
                  <th className="px-3 py-2 text-right font-normal">手数(雇员)</th>
                  <th className="px-3 py-2 text-left font-normal">模式</th>
                  <th className="px-3 py-2 text-left font-normal">主账户</th>
                  <th className="px-3 py-2 text-left font-normal">状态</th>
                  <th className="px-3 py-2 text-left font-normal">原因</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s, i) => (
                  <SignalRow key={s.id} signal={s} fresh={page === 1 && i < realtime.length} />
                ))}
                {items.length === 0 && !listQ.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">
                      没有符合条件的信号
                    </td>
                  </tr>
                )}
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-ink-3">
                      加载中...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-line">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || listQ.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </Button>
              <span className="text-xs text-ink-3 num">{page} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || listQ.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                下一页
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
